<template>
  <div class="admin-dashboard-page">
    <!-- Header -->
    <div class="viva-page-header-with-action">
      <div class="viva-header-content">
        <img src="/viva/viva_logo.svg" alt="Viva" class="viva-logo" />
      </div>
      <ProfileDropdown
        :student-name="adminName"
        current-portal="admin"
        :disable-portal-link="!isVivaInstructor"
      />
    </div>

    <!-- Page Title Section -->
    <div class="instructor-info" style="margin-bottom: 10px;">
      <h1 class="viva-page-title" style="margin: 0;">ADMIN DASHBOARD</h1>
    </div>

    <!-- Main Card -->
    <div class="admin-container">
      <div class="admin-card">
        <!-- Info Header -->
        <div class="admin-info-header">
          <div class="admin-header-content">
            <div>
              <h3 class="admin-title">{{ schoolName || 'Your School' }}</h3>
              <p class="admin-subtitle">Manage instructor accounts at your organization</p>
            </div>
            <button @click="openAddModal" class="viva-btn viva-btn-green viva-btn-small">
              <i class="fa fa-plus"></i> Add Instructor
            </button>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="loading-container">
          <i class="fa fa-spinner fa-spin" style="font-size: 24px; color: var(--viva-green);"></i>
          <p>Loading instructors...</p>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="loading-container">
          <p>{{ error }}</p>
          <button @click="fetchTeachers" class="viva-btn viva-btn-green viva-btn-small">Try Again</button>
        </div>

        <!-- Empty State -->
        <div v-else-if="teachers.length === 0" class="empty-state">
          <i class="fa fa-users" style="font-size: 40px; color: var(--viva-green);"></i>
          <h3>No Instructors Yet</h3>
          <p>Get started by adding instructors to your school.</p>
        </div>

        <!-- Teachers Table -->
        <div v-else class="teachers-table-wrapper">
          <div class="table-scroll-container">
            <table class="teachers-table">
              <thead>
                <tr>
                  <th class="name-col">NAME</th>
                  <th class="email-col">EMAIL</th>
                  <th class="status-col">STATUS</th>
                  <th class="actions-col">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="teacher in teachers" :key="teacher.id"
                    :class="{ 'inactive-row': !teacher.r_active }">
                  <td class="name-col">{{ teacher.first_name }} {{ teacher.last_name }}</td>
                  <td class="email-col">{{ teacher.email }}</td>
                  <td class="status-col">
                    <span v-if="teacher.r_active" class="status-badge status-active">Active</span>
                    <span v-else class="status-badge status-inactive">Inactive</span>
                  </td>
                  <td class="actions-col">
                    <button v-if="teacher.r_active"
                            @click="openRevokeModal(teacher)"
                            class="viva-btn viva-btn-small viva-btn-orange"
                            :disabled="actionLoading === teacher.id">
                      {{ actionLoading === teacher.id ? 'Processing...' : 'Revoke Access' }}
                    </button>
                    <span v-else class="inactive-label">
                      Waiting for approval
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Instructor Modal -->
    <div v-if="showAddModal" class="viva-modal-backdrop" @click="showAddModal = false">
      <div class="viva-modal-content" @click.stop>
        <div class="viva-modal-header">
          <h2>Add Instructor</h2>
          <button type="button" class="close" @click="showAddModal = false" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="viva-modal-body">
          <p style="margin-bottom: 20px;">This instructor will be added to your school and will need approval from our team before they can log in.</p>
          <div class="form-group">
            <input type="text" v-model="addForm.firstName" placeholder="First Name" class="form-input">
          </div>
          <div class="form-group">
            <input type="text" v-model="addForm.lastName" placeholder="Last Name" class="form-input">
          </div>
          <div class="form-group">
            <input type="email" v-model="addForm.email" placeholder="Email" class="form-input">
          </div>
          <div v-if="addError" class="alert-box alert-danger">
            {{ addError }}
          </div>
          <div v-if="addSuccess" class="alert-box alert-success">
            {{ addSuccess }}
          </div>
        </div>
        <div class="viva-modal-footer">
          <button
            @click="addInstructor"
            class="viva-btn viva-btn-green"
            :disabled="!addFormValid || addLoading"
          >
            {{ addLoading ? 'Adding...' : 'Add Instructor' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Revoke Access Confirmation Modal -->
    <div v-if="showRevokeModal" class="viva-modal-backdrop" @click="closeRevokeModal">
      <div class="viva-modal-content" @click.stop>
        <div class="viva-modal-header">
          <h2>Revoke Access?</h2>
          <button type="button" class="close" @click="closeRevokeModal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="viva-modal-body">
          <p style="margin-bottom: 15px;">
            Are you sure you want to revoke access for
            <strong>{{ teacherToRevoke ? (teacherToRevoke.first_name + ' ' + teacherToRevoke.last_name) : '' }}</strong>
            ({{ teacherToRevoke ? teacherToRevoke.email : '' }})?
          </p>
          <p style="margin-bottom: 0;">
            This instructor will lose access immediately and will need to be reactivated by our team. They will no longer be able to log in or access their courses.
          </p>
        </div>
        <div class="viva-modal-footer" style="display: flex; justify-content: space-between;">
          <button
            type="button"
            @click="closeRevokeModal"
            class="viva-btn viva-btn-small viva-btn-gray"
            :disabled="teacherToRevoke && actionLoading === teacherToRevoke.id"
          >
            Cancel
          </button>
          <button
            type="button"
            @click="confirmRevoke"
            class="viva-btn viva-btn-small viva-btn-orange"
            :disabled="teacherToRevoke && actionLoading === teacherToRevoke.id"
          >
            {{ teacherToRevoke && actionLoading === teacherToRevoke.id ? 'Revoking...' : 'Yes, Revoke Access' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '../utils/apiClient.js'
import ProfileDropdown from '@/components/ProfileDropdown.vue'
import { getStatusData } from '../utils/statusCache.js'

export default {
  name: 'AdminDashboard',
  components: {
    ProfileDropdown
  },
  data() {
    return {
      loading: true,
      error: null,
      teachers: [],
      actionLoading: null,
      adminName: '',
      isVivaInstructor: false,
      schoolName: null,
      showAddModal: false,
      addForm: {
        firstName: '',
        lastName: '',
        email: ''
      },
      addError: null,
      addSuccess: null,
      addLoading: false,
      showRevokeModal: false,
      teacherToRevoke: null
    }
  },
  computed: {
    addFormValid() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return this.addForm.firstName.trim() && this.addForm.lastName.trim() && emailRegex.test(this.addForm.email.trim())
    }
  },
  mounted() {
    this.fetchTeachers()
    const statusData = getStatusData()
    if (statusData) {
      this.adminName = statusData.student_name || ''
      this.isVivaInstructor = statusData.is_viva_instructor || false
    }
  },
  methods: {
    async fetchTeachers() {
      this.loading = true
      this.error = null
      try {
        const response = await apiClient.get('/api/viva/admin/teachers/')
        this.teachers = response.data.teachers || []
        this.schoolName = response.data.school_name || null
      } catch (err) {
        console.error('Error fetching teachers:', err)
        this.error = 'Failed to load teachers. Please try again.'
      } finally {
        this.loading = false
      }
    },

    openAddModal() {
      this.addForm = { firstName: '', lastName: '', email: '' }
      this.addError = null
      this.addSuccess = null
      this.showAddModal = true
    },

    openRevokeModal(teacher) {
      this.teacherToRevoke = teacher
      this.showRevokeModal = true
    },

    closeRevokeModal() {
      // Don't allow closing while a revoke is in flight
      if (this.teacherToRevoke && this.actionLoading === this.teacherToRevoke.id) return
      this.showRevokeModal = false
      this.teacherToRevoke = null
    },

    async confirmRevoke() {
      if (!this.teacherToRevoke) return
      const teacher = this.teacherToRevoke
      this.actionLoading = teacher.id
      try {
        await apiClient.post('/api/viva/admin/revoke_teacher/', { user_id: teacher.id })
        teacher.r_active = false
        this.actionLoading = null
        this.showRevokeModal = false
        this.teacherToRevoke = null
      } catch (err) {
        console.error('Error revoking access:', err)
        alert('Failed to revoke access. Please try again.')
        this.actionLoading = null
      }
    },

    async addInstructor() {
      this.addLoading = true
      this.addError = null
      this.addSuccess = null
      try {
        const response = await apiClient.post('/api/viva/admin/add_instructor/', {
          first_name: this.addForm.firstName.trim(),
          last_name: this.addForm.lastName.trim(),
          email: this.addForm.email.trim()
        })
        this.addSuccess = response.data.message
        this.addForm = { firstName: '', lastName: '', email: '' }
        this.fetchTeachers()
      } catch (err) {
        console.error('Error adding instructor:', err)
        this.addError = err.response?.data?.error || 'Failed to add instructor. Please try again.'
      } finally {
        this.addLoading = false
      }
    }
  }
}
</script>

<style scoped>
@import '../styles/viva-classes.css';

.admin-dashboard-page {
  background-color: var(--viva-cream);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Container - matches submissions-container pattern */
.admin-container {
  margin: 0 60px;
  padding: 0 0 40px 0;
  flex: 1;
  box-sizing: border-box;
}

.admin-card {
  background: var(--viva-white);
  border: 3px solid var(--viva-green);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

/* Info Header - matches assignment-info-header pattern */
.admin-info-header {
  background: var(--viva-green);
  padding: 20px 30px;
}

.admin-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.admin-title {
  color: var(--viva-white);
  font-size: 22px;
  font-weight: 700;
  margin: 0;
}

.admin-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;
  margin: 4px 0 0 0;
}

.admin-info-header .viva-btn-green {
  background: var(--viva-white);
  color: var(--viva-green);
  font-weight: 600;
}

.admin-info-header .viva-btn-green:hover {
  background: var(--viva-cream);
  color: var(--viva-green);
}

/* Loading & Empty */
.loading-container,
.empty-state {
  text-align: center;
  padding: 50px 20px;
}

.empty-state h3 {
  color: var(--viva-dark);
  margin: 15px 0 8px 0;
  font-size: 20px;
}

/* Teachers Table - matches submissions-table pattern */
.teachers-table-wrapper {
  margin: 0;
}

.table-scroll-container {
  overflow-x: auto;
}

.teachers-table {
  width: 100%;
  border-collapse: collapse;
}

.teachers-table thead {
  background-color: var(--viva-cream);
}

.teachers-table th {
  padding: 15px 20px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.5px;
  color: var(--viva-dark);
  border-bottom: 2px solid var(--viva-green);
}

.teachers-table td {
  padding: 15px 20px;
  border-bottom: 1px solid #e0e0e0;
  font-size: 14px;
  color: var(--viva-dark);
}

.teachers-table tbody tr:hover {
  background-color: #f9f9f9;
}

.teachers-table tbody tr:last-child td {
  border-bottom: none;
}

/* Inactive row - grey out text but keep actions visible */
.inactive-row .name-col,
.inactive-row .email-col {
  color: var(--viva-grey);
  opacity: 0.5;
}

.inactive-label {
  font-size: 13px;
  color: var(--viva-grey);
  font-weight: 600;
  font-style: italic;
}

/* Column widths */
.name-col { width: 25%; }
.email-col { width: 30%; }
.status-col { width: 15%; }
.actions-col { width: 30%; }

/* Status Badges */
.status-badge {
  padding: 4px 14px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-active {
  background: #d4edda;
  color: #155724;
}

.status-inactive {
  background: #f8d7da;
  color: #721c24;
}

/* Alert Boxes */
.alert-box {
  padding: 10px 15px;
  border-radius: 8px;
  margin-top: 10px;
  font-size: 14px;
}

.alert-success {
  background: #d4edda;
  color: #155724;
}

.alert-danger {
  background: #f8d7da;
  color: #721c24;
}

@media (max-width: 767px) {
  .admin-header-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .admin-container {
    padding: 0 15px 30px 15px;
  }

  .name-col, .email-col, .status-col, .actions-col {
    width: auto;
  }
}
</style>
